import React, { useRef, useState, DragEvent } from 'react';
import classNames from 'classnames';
import { UploadRegularIcon } from 'src/monet/icons';

export type MultiFileUploadFormProps = {
  onNewFile: (files: File[]) => void;
  accept?: string;
  containerClassName?: string;
  label?: string;
};

export const MultiFileUploadForm: React.FC<MultiFileUploadFormProps> = ({
  onNewFile,
  accept,
  containerClassName = 'h-[88px] px-6 py-4',
  label = 'Upload resource',
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showDrag, setShowDrag] = useState(false);

  const handleSelectFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files && files.length > 0) {
      const filesArray = Array.from(files);
      setUploading(true);
      onNewFile(filesArray);
      setUploading(false);
      // Reset the input so the same file can be uploaded again if needed
      if(fileInputRef.current){
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDrag(false);
    const dt = e.dataTransfer;
    if (dt && dt.files && dt.files.length > 0) {
      const filesArray = Array.from(dt.files);
      setUploading(true);
      onNewFile(filesArray);
      setUploading(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDrag(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDrag(false);
  };

  const handleMouseEnter = () => {
    setShowDrag(true);
  };

  const handleMouseLeave = () => {
    setShowDrag(false);
  };

  const onClick = () => {
    if (!uploading && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div
      className={classNames(
        'group flex justify-center items-center rounded-lg transition cursor-pointer relative',
        containerClassName,
        {
          'hover:bg-neue-journey-accent-10': !uploading,
          'bg-neue-journey-accent-10': showDrag,
          'bg-neue-journey-fg-10': !showDrag,
        }
      )}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        type="file"
        multiple
        onChange={handleSelectFiles}
        accept={accept}
        ref={fileInputRef}
        style={{ display: 'none' }}
      />
      {uploading ? (
        <div>Loading...</div>
      ) : (
        <div className="flex items-center gap-2 transition-colors text-neue-journey-fg-50 group-hover:text-neue-journey-fg">
          <UploadRegularIcon className="w-4 h-4 shrink-0" />
          <div className="text-neue-journey-small">{label}</div>
        </div>
      )}
    </div>
  );
}; 